import axios from "axios";

import {
    IAdmin,
    IExternalListPage,
    IProduct,
    IProductVariant,
} from "../../types/api";

export enum StockStatus {
    IN_STOCK = 1,
    OOS = 2,
}

export enum StockStatusSource {
    SYNC = 1,
    MANUAL = 2,
}

export enum StockOverrideReason {
    INCORRECT_SYNC = 1,
    QUALITY_HOLD = 2,
    DISCONTINUED = 3,
    NOT_LAUNCHED = 4,
    OTHER = 5,
}

export const stockOverrideReasonMap = {
    [StockOverrideReason.INCORRECT_SYNC]: "Incorrect sync",
    [StockOverrideReason.QUALITY_HOLD]: "Quality hold",
    [StockOverrideReason.DISCONTINUED]: "Discontinued",
    [StockOverrideReason.NOT_LAUNCHED]: "Not launched",
    [StockOverrideReason.OTHER]: "Other",
};

export interface IVariantThresholdOverride {
    floorCap: number | null;
    restockCap: number | null;
    active: boolean;
    updatedBy: string | null;
    updatedAt: string;
}

export interface IVariantInventory {
    variant: IProductVariant;
    product: IProduct | null;
    quantity: number | null;
    syncedAt: string | null;
    stale: boolean;
    sharedVariantCount: number;
    status: StockStatus;
    source: StockStatusSource;
    overrideExpiresAt: string | null;
    overrideActive: boolean;
    lastEvaluatedAt: string | null;
    lastChangedAt: string | null;
    floorCap: number;
    restockCap: number;
    thresholdsOverridden: boolean;
    override: IVariantThresholdOverride | null;
    defaultFloorCap: number;
    defaultRestockCap: number;
    stalenessLimit: number;
}

export interface IStockStatusHistoryEntry {
    id: string;
    variantId: string;
    status: StockStatus;
    source: StockStatusSource;
    createdBy: IAdmin | null;
    triggerQuantity: number | null;
    effectiveCap: number | null;
    reason: StockOverrideReason | null;
    note: string;
    createdAt: string;
}

export interface IGetVariantInventoryParameters {
    variantId: string;
    token: string;
}

export interface IGetVariantStockHistoryParameters {
    variantId: string;
    limit: number;
    cursor?: string;
    direction?: string;
    token: string;
}

export interface ISetStatusOverrideParameters {
    variantId: string;
    status: StockStatus;
    /** Null holds the override until an admin releases it. */
    expiresAt: string | null;
    reason: StockOverrideReason;
    /** Required by the API when the reason is OTHER. */
    note: string;
    token: string;
}

export interface IClearStatusOverrideParameters {
    variantId: string;
    token: string;
}

export interface IUpdateVariantThresholdsParameters {
    variantId: string;
    /** Null inherits the global default; omit to leave the stored value. */
    floorCap?: number | null;
    restockCap?: number | null;
    active?: boolean;
    token: string;
}

const headers = (token: string) => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
    "Admin-Request": "true",
});

export class Inventory {
    static async getVariantInventory(
        parameters: IGetVariantInventoryParameters,
    ): Promise<IVariantInventory> {
        const { variantId, token } = parameters;

        const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/inventory/variants/${variantId}`,
            { headers: headers(token) },
        );

        return data;
    }

    static async getVariantStockHistory(
        parameters: IGetVariantStockHistoryParameters,
    ): Promise<IExternalListPage<IStockStatusHistoryEntry>> {
        const { variantId, limit, cursor, direction, token } = parameters;

        const params = new URLSearchParams();

        if (limit) {
            params.append("limit", limit.toString());
        }

        if (cursor) {
            params.append("cursor", cursor);
        }

        if (direction) {
            params.append("direction", direction);
        }

        const { data } = await axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/inventory/variants/${variantId}/history?${params}`,
            { headers: headers(token) },
        );

        return data;
    }

    static async setStatusOverride(
        parameters: ISetStatusOverrideParameters,
    ): Promise<IVariantInventory> {
        const { variantId, token, ...body } = parameters;

        const { data } = await axios.patch(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/inventory/variants/${variantId}/status-override`,
            body,
            { headers: headers(token) },
        );

        return data;
    }

    static async clearStatusOverride(
        parameters: IClearStatusOverrideParameters,
    ): Promise<IVariantInventory> {
        const { variantId, token } = parameters;

        const { data } = await axios.delete(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/inventory/variants/${variantId}/status-override`,
            { headers: headers(token) },
        );

        return data;
    }

    static async updateThresholds(
        parameters: IUpdateVariantThresholdsParameters,
    ): Promise<IVariantInventory> {
        const { variantId, token, ...body } = parameters;

        const { data } = await axios.patch(
            `${process.env.REACT_APP_API_BASE_URL}/api/admins/v1/inventory/variants/${variantId}/thresholds`,
            body,
            { headers: headers(token) },
        );

        return data;
    }
}
